/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ThreadTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { mainColors } from '../../helpers/colors';
import ExportData from '../ExportData/ExportData';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash } from '@coreui/icons';
import { deleteThreadRequestAction } from '../../store/thread/threadActions';
import { useTranslation } from '../../hooks/useTranslation';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const ThreadTable = (props) => {
  const { 
    threadList,
    threadData,
    changeSortType,
    changeSortKey,
    threadHistorySortData,
    deleteThreadRequest
  } = props;
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index','ThreadId','ThreadName','ThreadDescription','actions'], 'ThreadTable');
  
  const columns = [
    { id: 'index', label: '#', sortable: false },
    { id: 'ThreadId', label: t('id'), sortable: true },
    { id: 'ThreadName', label: t('threadName'), sortable: true },
    { id: 'ThreadDescription', label: t('Description'), sortable: true },
    { id: 'actions', label: t('Actions'), sortable: false }
  ];

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  useEffect(() => {
    if (threadList && Array.isArray(threadList)) {
      setTotalRows(threadList.length);
      setTableData(threadList);
    }
  }, [threadList]);

  const handleEdit = (item) => {
    console.log('Editing thread:', item);
    navigate(`/thread/edit/${item.ThreadId}`, {
      state: { thread: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          ThreadId: deleteModal.item.ThreadId
        };

        console.log('Deleting thread:', deleteData);
        deleteThreadRequest({ data: deleteData });
      } else {
        console.error('User session not found');
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    scrollToTableContainer();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    if (changeSortType) changeSortType(newOrder);
    if (changeSortKey) changeSortKey(property);
  };

  function scrollToTableContainer() {
    const tableContainer = document.querySelector('.thread-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const getSortedData = () => {
    const stabilizedData = stableSort(tableData, getComparator(order, orderBy));
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return stabilizedData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);
  
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem 
        key={i} 
        active={i === page} 
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }
  
  const exportColumns = React.useMemo(() => columns.filter(c => !['actions','index'].includes(c.id)), [columns]);
  const buildCellValue = (colId, item) => item[colId] ?? '';

  return (
    <div className="thread-table-container">
      <CCard className="mb-4">
        <CCardBody>
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted">
                {totalRows} {t('resultsFound')}
              </span>
              <ExportData
                columns={exportColumns}
                rows={getSortedData()}
                allRows={stableSort(threadList, getComparator(order, orderBy))}
                buildCellValue={buildCellValue}
                filenamePrefix="threads_export"
                triggerDisabled={totalRows === 0}
              />
            </div>
          )}
          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell 
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }}
                    >
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon
                          icon={order === 'asc' ? cilArrowTop : cilArrowBottom}
                          size="sm"
                          className="ms-1"
                        />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.ThreadId || index}>
                    <CTableDataCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.ThreadId}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {item.ThreadName}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span title={item.ThreadDescription}>
                        {item.ThreadDescription?.length > 50 
                          ? `${item.ThreadDescription.substring(0, 50)}...`
                          : item.ThreadDescription
                        }
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('updateThreadStatus')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleEdit(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilPencil}
                              size="sm"
                              style={{ color: mainColors.primary }}
                            />
                          </div>
                        </CTooltip>
                        <CTooltip content={t('deleteThreadStatus')}>
                          <div 
                            className="cursor-pointer p-1"
                            onClick={() => handleDeleteClick(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilTrash}
                              size="sm"
                              style={{ color: '#dc3545' }}
                            />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center">
                    {t('noDataFound')}
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select 
                  className="form-select form-select-sm" 
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              
              <CPagination className="mb-0">
                <CPaginationItem
                  onClick={() => page > 1 && handleChangePage(page - 1)}
                  disabled={page === 1}
                >
                  &laquo;
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem
                  onClick={() => page < totalPages && handleChangePage(page + 1)}
                  disabled={page === totalPages}
                >
                  &raquo;
                </CPaginationItem>
              </CPagination>

              <div className="text-muted">
                {t('paginationInfo', {
                  start: Math.min((page - 1) * rowsPerPage + 1, totalRows),
                  end: Math.min(page * rowsPerPage, totalRows),
                  total: totalRows
                })}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteThreadStatus')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteThreadStatus')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('statusName')}:</strong> {deleteModal.item.ThreadName}<br/>
              <strong>{t('description')}:</strong> {deleteModal.item.ThreadDescription}<br/>
              <strong>ID:</strong> {deleteModal.item.ThreadId}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('thisActionCannotBeUndone')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            {t('delete')}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    threadList: state.threadReducer?.threadList || [],
    threadData: state.threadReducer?.threadData || null,
    threadHistorySortData: state.threadReducer?.threadHistorySortData || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteThreadRequest: (payload) => {
      dispatch(deleteThreadRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ThreadTable);